import React, { useEffect } from 'react';
import {
  Checkbox,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import { Product } from '../../../types/product';
import { formatFullDate } from '../../../utils/dateUtils';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

interface ProductFormProps {
  form: FormInstance;
  initialValues?: Product | null;
  product?: Product | null;
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  form,
  initialValues,
  product,
  onSubmit,
}) => {
  const isEdit = !!(product || initialValues);
  const productData = product || initialValues;

  useEffect(() => {
    if (productData) {
      form.setFieldsValue({
        ...productData,
        tagsText: Array.isArray(productData.tags) ? productData.tags.join('，') : '',
        forbiddenRegionsText: Array.isArray(productData.forbidden_regions)
          ? productData.forbidden_regions.join('，')
          : '',
      });
    } else {
      form.resetFields();
    }
  }, [form, productData]);

  const formContainerStyle: React.CSSProperties = {
    maxHeight: '65vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 12px 12px 0',
  };

  return (
    <div style={formContainerStyle}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          operator: '移动',
          category: '流量卡',
          status: '在售',
          external_status: '在售',
          inventory: 9999,
          sales: 0,
          need_shipping: true,
          need_id_card: true,
          need_id_card_pic: true,
          need_face_pic: true,
          need_verify_code: false,
          need_payment: false,
          support_number_selection: false,
          is_featured: false,
          is_broadband: false,
          is_pinned: false,
        }}
        id="productForm"
        onFinish={async (values) => {
          const {
            id,
            tagsText,
            forbiddenRegionsText,
            ...rest
          } = values;

          const submitData = {
            ...rest,
            tags: tagsText
              ? String(tagsText)
                  .split(/[,，、\n]/)
                  .map((item) => item.trim())
                  .filter(Boolean)
              : [],
            forbidden_regions: forbiddenRegionsText
              ? String(forbiddenRegionsText)
                  .split(/[,，、\n]/)
                  .map((item) => item.trim())
                  .filter(Boolean)
              : [],
          };

          await onSubmit(submitData);
        }}
      >
        {isEdit && (
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="产品名称"
              rules={[{ required: true, message: '请输入产品名称' }]}
            >
              <Input placeholder="产品名称" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="subtitle" label="副标题">
              <Input placeholder="副标题" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="operator"
              label="运营商类型"
              rules={[{ required: true, message: '请选择运营商类型' }]}
            >
              <Select placeholder="选择运营商类型">
                <Option value="移动">移动</Option>
                <Option value="电信">电信</Option>
                <Option value="联通">联通</Option>
                <Option value="广电">广电</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="category" label="产品分类">
              <Select allowClear placeholder="选择产品分类">
                <Option value="流量卡">流量卡</Option>
                <Option value="普通卡">普通卡</Option>
                <Option value="宽带">宽带</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="状态"
              name="status"
              rules={[{ required: true, message: '请选择状态' }]}
            >
              <Select>
                <Option value="在售">在售</Option>
                <Option value="已下架">已下架</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="外部状态"
              name="external_status"
              rules={[{ required: true, message: '请选择外部状态' }]}
            >
              <Select>
                <Option value="在售">外部在售</Option>
                <Option value="已下架">外部下架</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="source_platform" label="来源平台">
              <Select allowClear placeholder="选择来源平台">
                <Option value="manual">手动</Option>
                <Option value="haoyi">号易</Option>
                <Option value="172">172平台</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="source_product_id" label="外部产品ID">
              <Input placeholder="外部平台产品ID" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="image_main" label="主图">
              <Input placeholder="主图 URL" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="image_detail" label="详情图">
          <Input placeholder="详情图 URL" />
        </Form.Item>

        <h3>资费套餐</h3>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="monthly_original" label="原月租">
              <InputNumber style={{ width: '100%' }} placeholder="原月租" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="monthly_current" label="当前月租">
              <InputNumber style={{ width: '100%' }} placeholder="当前月租" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="face_value" label="面值">
              <InputNumber style={{ width: '100%' }} placeholder="面值" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="traffic_general" label="通用流量">
              <Input placeholder="如 155G" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="traffic_directional" label="定向流量">
              <Input placeholder="如 30G" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="voice" label="通话时长">
              <Input placeholder="如 100分钟" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="charge_rule" label="首充规则">
          <TextArea rows={3} placeholder="首充规则" />
        </Form.Item>

        <Form.Item name="package_note" label="套餐说明">
          <TextArea rows={3} placeholder="套餐说明" />
        </Form.Item>

        <h3>佣金结算</h3>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="settle_price" label="结算价">
              <InputNumber style={{ width: '100%' }} placeholder="结算价" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="commission_type" label="返佣类型">
              <Input placeholder="返佣类型" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="contract_period" label="合约期">
              <Input placeholder="如 12个月" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="commission_level1" label="一级佣金">
              <InputNumber style={{ width: '100%' }} placeholder="一级佣金" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="commission_level2" label="二级佣金">
              <InputNumber style={{ width: '100%' }} placeholder="二级佣金" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="commission_level3" label="三级佣金">
              <InputNumber style={{ width: '100%' }} placeholder="三级佣金" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="settle_rule" label="结算规则">
          <TextArea rows={3} placeholder="结算规则" />
        </Form.Item>

        <h3>限制与配送</h3>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="age_min" label="最小年龄">
              <InputNumber style={{ width: '100%' }} placeholder="最小年龄" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="age_max" label="最大年龄">
              <InputNumber style={{ width: '100%' }} placeholder="最大年龄" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="shipping_region_type" label="地区限制类型">
              <Select allowClear placeholder="选择限制类型">
                <Option value="whitelist">白名单</Option>
                <Option value="blacklist">黑名单</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="forbiddenRegionsText" label="地区限制">
          <TextArea rows={3} placeholder="多个地区用逗号分隔" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="express_method" label="快递方式">
              <Input placeholder="如 京东/顺丰" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="activation_method" label="激活方式">
              <Input placeholder="激活方式" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="activation_flow_image" label="激活流程图">
          <Input placeholder="激活流程图 URL" />
        </Form.Item>

        <Form.Item name="activation_guide" label="激活指引">
          <TextArea rows={3} placeholder="激活指引" />
        </Form.Item>

        <h3>下单配置</h3>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="need_shipping" valuePropName="checked">
              <Checkbox>需要发货</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="need_id_card" valuePropName="checked">
              <Checkbox>需要身份证号</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="need_id_card_pic" valuePropName="checked">
              <Checkbox>需要身份证图片</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="need_face_pic" valuePropName="checked">
              <Checkbox>需要人像照</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="need_verify_code" valuePropName="checked">
              <Checkbox>需要验证码</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="need_payment" valuePropName="checked">
              <Checkbox>需要在线付款</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="support_number_selection" valuePropName="checked">
              <Checkbox>支持选号</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="is_featured" valuePropName="checked">
              <Checkbox>主推产品</Checkbox>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="is_broadband" valuePropName="checked">
              <Checkbox>宽带产品</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <h3>运营信息</h3>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="inventory"
              label="库存"
              rules={[{ required: true, message: '请输入库存' }]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="库存" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="sales"
              label="销量"
              rules={[{ required: true, message: '请输入销量' }]}
            >
              <InputNumber style={{ width: '100%' }} placeholder="销量" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="keywords" label="关键词">
          <Input placeholder="搜索关键词" />
        </Form.Item>

        <Form.Item name="tagsText" label="标签">
          <Input placeholder="多个标签用逗号分隔" />
        </Form.Item>

        <Form.Item name="remark" label="备注">
          <TextArea rows={3} placeholder="备注" />
        </Form.Item>

        {initialValues && initialValues.id && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Form.Item label="创建时间">
                <Text>{formatFullDate(initialValues.createdAt)}</Text>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="最后更新时间">
                <Text>{formatFullDate(initialValues.updatedAt)}</Text>
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </div>
  );
};

export default ProductForm;
